import { apiClient } from './client';

/**
 * Hardware allocation by department — which departments may request which
 * device (Phase 11, migration 077).
 *
 * ⛔ A device is restricted if, and only if, it has at least one allocation.
 * There is no flag. So the FIRST allocation on a device is what restricts it,
 * and removing the LAST one un-restricts it.
 */

export interface HwAllocation {
  DeviceDepartmentId: number;
  DeviceOptionId: number;
  DeviceName: string;
  DepartmentCode: string;
  /** What Oscar calls it now. Null when the department has been retired. */
  DepartmentDescription: string | null;
  /**
   * ⚠ Retired in Oscar since this was written. The row still works —
   * eligibility joins on the CODE — but nobody is in that department any more,
   * so it entitles nobody.
   */
  DepartmentIsRetired: boolean;
  Note: string | null;
}

export interface HwAllocDevice {
  DeviceOptionId: number;
  Name: string;
  IsActive: boolean;

  /**
   * Kit for a LOCATION, not a person (060).
   *
   * ⛔ Which makes a department meaningless on it. A department is an attribute
   * of a person, and since 06/10/2026 branch kit is not offered on a personal
   * request at all — so an allocation here would narrow a list nobody is ever
   * shown.
   *
   * ⚠ Still served, so the board can keep showing one that ALREADY carries an
   * allocation. Hiding it outright would hide the row with it and leave
   * something in the database nobody could reach.
   */
  IsBranchUse: boolean;

  /**
   * What makes the variants of a thing siblings (090) — "Laptop" over Standard,
   * High Spec, OLT and Tough Book.
   *
   * ⛔ A role gets ONE from each group, refused server-side. Null is ungrouped,
   * and an ungrouped device clashes with nothing.
   */
  DeviceGroup: string | null;

  /**
   * The variant a role falls back to when nothing in its group is allocated.
   *
   * ⛔ A group with allocations and NO default is one some roles cannot request
   * at all — 36 of 89 departments could ask for a laptop on 07/10/2026.
   */
  IsGroupDefault: boolean;
}

export interface HwAllocDepartment {
  DepartmentCode: string;
  Description: string;
  BusinessUnitCode: string | null;
}

export interface HwAllocationBoard {
  Allocations: HwAllocation[];
  Devices: HwAllocDevice[];
  Departments: HwAllocDepartment[];
}

/** GET /api/hardware-allocations — everything both lenses need, in one trip. */
export const getHardwareAllocations = (signal?: AbortSignal) =>
  apiClient.get<HwAllocationBoard>('/api/hardware-allocations', signal);

/**
 * POST /api/hardware-allocations — entitle a department to a device (admin).
 *
 * ⛔ `DeviceBecameRestricted` is true when this was the FIRST allocation for
 * that device — the one that changed the world, because before it the device
 * was offered to everybody.
 */
export const allocateHardwareDevice = (body: {
  DeviceOptionId: number;
  DepartmentCode: string;
  Note?: string | null;
}) =>
  apiClient.post<{ DeviceDepartmentId: number; DeviceBecameRestricted: boolean }>(
    '/api/hardware-allocations', body);

/**
 * POST /api/hardware-allocations/:id/remove — take it away (admin).
 *
 * ⚠ `DeviceIsNowUnrestricted` is true when that was the LAST one, so the device
 * goes back to being offered to everybody — the opposite of what "remove"
 * sounds like, which is why the server says it rather than leaving the page to
 * infer it.
 */
export const removeHardwareAllocation = (id: number) =>
  apiClient.post<{ Removed: boolean; DeviceIsNowUnrestricted: boolean }>(
    `/api/hardware-allocations/${id}/remove`);
